import { BasisBadge } from "@app/components/basis-switcher";
import { basisParam, getBasis } from "@app/lib/basis";
import BudgetToolbar from "@app/lib/budgets/budget-toolbar";
import { useBudgetEditor } from "@app/lib/budgets/use-budget-editor";
import type { XlsxCell, XlsxTable } from "@app/lib/export-xlsx";
import { useToast } from "@app/components/toast";
import { getPerspectiveId } from "@app/lib/perspective";
import CategoryDetailPanel from "@app/pages/categorize/category-detail-panel";
import SplitEditor, { type SliceAllocation } from "@app/pages/categorize/split-editor";
import StakeholderDetailPanel from "@app/pages/categorize/stakeholder-detail-panel";
import MonthDetail from "@app/pages/dashboard/month-detail";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { categorizationOverviewPage_Query } from "@app/queries/categorizationOverviewPage_Query.graphql";
import type { categorizationOverviewPage_CategorizeMutation } from "@app/queries/categorizationOverviewPage_CategorizeMutation.graphql";
import OverviewGrid, { type CostNode } from "./overview-grid";
import OwnerDistributionsRow from "./owner-distributions-row";

const categorizeMutation = graphql`
  mutation categorizationOverviewPage_CategorizeMutation(
    $input: CategorizeStakeholderInputObject!
  ) {
    categorizeStakeholder(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

export const overviewQuery = graphql`
  query categorizationOverviewPage_Query($perspectiveId: ID, $year: Int, $basis: BasisEnum) {
    perspective(perspectiveId: $perspectiveId) {
      id
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
      color
      parent {
        id
      }
    }
    perspectiveCostTree(perspectiveId: $perspectiveId, year: $year, basis: $basis) {
      nodeId
      parentNodeId
      kind
      name
      color
      depth
      sliceType
      sliceAmountCents
      sliceWeight
      category {
        id
        name
        color
      }
      stakeholder {
        id
        name
      }
      yearTotal
      points {
        month
        value
      }
    }
  }
`;

export const CURRENT_YEAR = new Date().getFullYear();

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const monthAbbr = (yyyymm: string) => MONTH_ABBR[Number(yyyymm.slice(5, 7)) - 1] ?? yyyymm;

// Inclusive last day of a "YYYY-MM" month, as YYYY-MM-DD.
const endOfMonth = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return `${month}-${String(last).padStart(2, "0")}`;
};

type Detail =
  | { kind: "category"; category: { id: string; name: string; color: string }; from: string; to: string }
  | { kind: "stakeholder"; stakeholder: { id: string; name: string }; month: string; highlightCategoryId: string | null }
  | { kind: "month"; month: string }
  | null;

export default function CategorizationOverviewPage({
  queryRef,
  initialBudgetId = null,
}: {
  queryRef: PreloadedQuery<categorizationOverviewPage_Query>;
  initialBudgetId?: string | null;
}) {
  const preloaded = usePreloadedQuery(overviewQuery, queryRef);
  const [nodes, setNodes] = useState(preloaded.perspectiveCostTree);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(false);
  // In-flight flag for a drag-drop / split save, so a dropped counterparty shows
  // a "Saving…" indicator instead of appearing to do nothing.
  const [saving, setSaving] = useState(false);
  const notify = useToast();
  const [detail, setDetail] = useState<Detail>(null);
  const [editingStakeholderId, setEditingStakeholderId] = useState<string | null>(null);
  const perspectiveId = getPerspectiveId();
  // The resolved perspective (handles the default when nothing is selected),
  // and its category tree — both year-independent, so taken from the initial load.
  const perspectiveRelayId = preloaded.perspective.id;
  const categories = preloaded.categories;

  // "Actuals vs Planned" editor for the cost side, anchored to the resolved
  // perspective + basis so a saved budget reloads under the same lens.
  const editor = useBudgetEditor({
    side: "COST",
    year,
    perspectiveId: perspectiveRelayId,
    basis: getBasis(),
  });

  // Re-sync the local tree only when the route hands a NEW query (e.g. a
  // perspective switch), keyed on queryRef. Do NOT key on `preloaded`: an
  // imperative refresh() fetches a different query root (it adds `year`) yet
  // overwrites the shared, id-normalized cost-tree records, which makes
  // usePreloadedQuery re-emit a snapshot whose (never-refetched) root list
  // still carries the stale `uncat-stk:<id>` node — clobbering the post-drop
  // tree and bouncing a just-categorized stakeholder back under Uncategorized.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setNodes(preloaded.perspectiveCostTree), [queryRef]);

  // Route a clicked month cell into the reused detail panels, scoped to that
  // month: category nodes → category panel (from/to), stakeholder leaves →
  // stakeholder panel (month), the totals row → all the month's transactions.
  const openCell = (node: CostNode, month: string) => {
    if (node.kind === "TOTALS") {
      setDetail({ kind: "month", month });
    } else if (node.kind === "CATEGORY" && node.category) {
      setDetail({ kind: "category", category: node.category, from: `${month}-01`, to: endOfMonth(month) });
    } else if (node.stakeholder) {
      setDetail({
        kind: "stakeholder",
        stakeholder: node.stakeholder,
        month,
        highlightCategoryId: node.category?.id ?? null,
      });
    }
  };

  const refresh = (nextYear = year) =>
    fetchQuery<categorizationOverviewPage_Query>(environment, overviewQuery, {
      perspectiveId: perspectiveId ?? undefined,
      year: nextYear,
      basis: basisParam(getBasis()) ?? undefined,
    })
      .toPromise()
      .then((fresh) => {
        if (fresh) setNodes(fresh.perspectiveCostTree);
      });

  const changeYear = (next: number) => {
    setYear(next);
    editor.selectBudget(null); // budgets are year-scoped — drop the loaded one
    setLoading(true);
    refresh(next).finally(() => setLoading(false));
  };

  // Deep-link from /budgets (?budget=<id>): switch into planned mode, load the
  // budget, and jump to its year (without clearing the just-loaded cells, which a
  // user-initiated year change would). The perspective is set in localStorage by
  // the /budgets page before navigation, so the cost tree already loaded the right
  // rows. Runs once on mount.
  useEffect(() => {
    if (!initialBudgetId) return;
    editor.setMode(true);
    editor.selectBudget(initialBudgetId).then((loaded) => {
      if (loaded && loaded.year !== year) {
        setYear(loaded.year);
        setLoading(true);
        refresh(loaded.year).finally(() => setLoading(false));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The visible cost tree as an actual / budget / delta grid (every node, in
  // display order), for the xlsx export. Year columns sum the typed monthly
  // budgets so the file stands alone.
  const buildBudgetExport = (): XlsxTable => {
    const months = nodes[0]?.points.map((point) => point.month) ?? [];
    const columns = [
      { label: "Category", type: "text" as const },
      ...months.flatMap((month) => [
        { label: `${monthAbbr(month)} Actual`, type: "money" as const },
        { label: `${monthAbbr(month)} Budget`, type: "money" as const },
        { label: `${monthAbbr(month)} Δ`, type: "money" as const },
      ]),
      { label: "Year Actual", type: "money" as const },
      { label: "Year Budget", type: "money" as const },
      { label: "Year Δ", type: "money" as const },
    ];
    const rows: XlsxCell[][] = nodes.map((node) => {
      const indent = "  ".repeat(Math.max(0, node.depth - 1));
      const cells: XlsxCell[] = [`${indent}${node.name}`];
      let yearBudget = 0;
      let hasBudget = false;
      for (const point of node.points) {
        const actual = point.value ?? 0;
        const budget = editor.budgetDollars(node.nodeId, point.month);
        if (budget !== null) {
          yearBudget += budget;
          hasBudget = true;
        }
        cells.push(actual, budget, budget === null ? null : actual - budget);
      }
      cells.push(node.yearTotal, hasBudget ? yearBudget : null, hasBudget ? node.yearTotal - yearBudget : null);
      return cells;
    });
    return { filename: `cost-budget-${year}`, sheetName: `Cost vs budget ${year}`, columns, rows };
  };

  // Reconstruct a stakeholder's full slice set from its leaf rows scattered
  // across the tree — the editor and drag re-home both edit this whole set.
  const sliceLeavesFor = (stakeholderId: string) =>
    nodes.filter((node) => node.kind === "STAKEHOLDER" && node.stakeholder?.id === stakeholderId);

  const buildEntry = (stakeholderId: string) => {
    const leaves = sliceLeavesFor(stakeholderId);
    const name =
      nodes.find((node) => node.stakeholder?.id === stakeholderId)?.stakeholder?.name ?? "";
    return {
      stakeholder: { name },
      allocations: leaves.map((leaf) => ({
        category: { id: leaf.category?.id ?? "" },
        sliceType: leaf.sliceType,
        amountCents: leaf.sliceAmountCents,
        weight: leaf.sliceWeight ?? 1,
      })),
    };
  };

  const applySlices = (
    stakeholderId: string,
    allocations: SliceAllocation[],
    successMessage = "Categorization saved"
  ) => {
    setSaving(true);
    commitMutation<categorizationOverviewPage_CategorizeMutation>(environment, {
      mutation: categorizeMutation,
      variables: { input: { stakeholderId, perspectiveId: perspectiveRelayId, allocations } },
      // Two error channels: transport/schema errors arrive as the second arg;
      // domain validation errors come back INSIDE the payload's `errors` field.
      // The old code only checked the former, so a rejected drop silently ran
      // refresh() as if it succeeded — the drop appeared to do nothing.
      onCompleted: (response, errors) => {
        setSaving(false);
        const failure =
          errors?.[0]?.message ??
          response.categorizeStakeholder?.errors?.[0]?.message;
        if (failure) {
          notify({ message: failure, kind: "error" });
          return;
        }
        notify({ message: successMessage, kind: "success" });
        refresh();
      },
      onError: (error) => {
        setSaving(false);
        notify({
          message: error.message || "Couldn't save — please try again.",
          kind: "error",
        });
      },
    });
  };

  // Drag a slice leaf onto a category: re-point it; a same-type slice already on
  // the target sums in; a different-type slice coexists (R3 grain). Persists the
  // whole re-pointed set through the single write path.
  const onSliceDrop = (dragged: CostNode, targetCategory: CostNode) => {
    const stakeholderId = dragged.stakeholder?.id;
    const targetCategoryId = targetCategory.category?.id;
    if (!stakeholderId || !targetCategoryId) return;

    // An Uncategorized row carries no slice of its own: append a REMAINDER that
    // routes the stakeholder's leftover into the target, preserving any existing
    // slices. A row only sits under Uncategorized when it has no remainder yet,
    // so the append is always collision-free.
    if (dragged.kind === "UNCATEGORIZED_STAKEHOLDER") {
      const existing: SliceAllocation[] = buildEntry(stakeholderId).allocations.map((allocation, index) => ({
        categoryId: allocation.category.id,
        sliceType: (allocation.sliceType ?? "PERCENTAGE") as SliceAllocation["sliceType"],
        ordinal: index,
        ...(allocation.sliceType === "ABSOLUTE" ? { amountCents: allocation.amountCents ?? undefined } : {}),
        ...(allocation.sliceType === "PERCENTAGE" ? { weight: allocation.weight } : {}),
      }));
      applySlices(
        stakeholderId,
        [
          ...existing,
          { categoryId: targetCategoryId, sliceType: "REMAINDER", ordinal: existing.length },
        ],
        `Moved ${dragged.stakeholder?.name ?? "counterparty"} → ${targetCategory.name}`
      );
      return;
    }

    const draggedType = dragged.sliceType ?? "PERCENTAGE";
    if (dragged.category?.id === targetCategoryId) {
      notify({ message: `Already in ${targetCategory.name}.`, kind: "info" });
      return;
    }

    const current = buildEntry(stakeholderId).allocations.map((allocation) => ({
      categoryId: allocation.category.id,
      sliceType: (allocation.sliceType ?? "PERCENTAGE") as SliceAllocation["sliceType"],
      amountCents: allocation.amountCents ?? undefined,
      weight: allocation.weight,
    }));

    const dragIndex = current.findIndex(
      (allocation) => allocation.categoryId === dragged.category?.id && allocation.sliceType === draggedType
    );
    if (dragIndex < 0) {
      notify({ message: "Couldn't find that allocation to move.", kind: "info" });
      return;
    }
    const [moving] = current.splice(dragIndex, 1);

    const resident = current.find(
      (allocation) => allocation.categoryId === targetCategoryId && allocation.sliceType === draggedType
    );
    if (resident) {
      if (draggedType === "ABSOLUTE") resident.amountCents = (resident.amountCents ?? 0) + (moving.amountCents ?? 0);
      else if (draggedType === "PERCENTAGE") resident.weight = Math.min(1, resident.weight + moving.weight);
      // a second REMAINDER simply collapses into the resident
    } else {
      current.push({ ...moving, categoryId: targetCategoryId });
    }

    const allocations: SliceAllocation[] = current.map((allocation, index) => ({
      categoryId: allocation.categoryId,
      sliceType: allocation.sliceType,
      ordinal: index,
      ...(allocation.sliceType === "ABSOLUTE" ? { amountCents: allocation.amountCents } : {}),
      ...(allocation.sliceType === "PERCENTAGE" ? { weight: allocation.weight } : {}),
    }));
    applySlices(
      stakeholderId,
      allocations,
      `Moved ${dragged.stakeholder?.name ?? "counterparty"} → ${targetCategory.name}`
    );
  };

  const hasCategories = nodes.some((node) => node.kind === "CATEGORY");

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-bold">Categorization overview</h1>
        <BasisBadge />
        <BudgetToolbar editor={editor} buildXlsx={buildBudgetExport} />
        <div className="ml-auto flex items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => changeYear(year - 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
            title="Previous year"
          >
            ‹
          </button>
          <span className="px-2 tabular-nums font-medium text-gray-800">
            {year}
          </span>
          <button
            type="button"
            disabled={year >= CURRENT_YEAR}
            onClick={() => changeYear(year + 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title="Next year"
          >
            ›
          </button>
        </div>
      </div>

      {/* The shared, perspective-independent "Owner distributions" panel (U0b):
          per-stakeholder dividend amounts for the selected year (excluded from
          the cost tree); editing the flag writes the structural distribution
          flag directly. A month cell drills into that stakeholder's month. */}
      <Suspense fallback={<div className="mb-4 h-14 animate-pulse rounded-lg bg-gray-100" />}>
        <OwnerDistributionsRow
          year={year}
          onOpenStakeholderMonth={(stakeholder, month) =>
            setDetail({ kind: "stakeholder", stakeholder, month, highlightCategoryId: null })
          }
        />
      </Suspense>

      {loading ? (
        <GridSkeleton />
      ) : !hasCategories ? (
        <EmptyPerspective />
      ) : (
        <OverviewGrid
          nodes={nodes}
          onCell={openCell}
          onEditSlice={(node) => node.stakeholder && setEditingStakeholderId(node.stakeholder.id)}
          onSliceDrop={onSliceDrop}
          budgetMode={editor.mode}
          getBudgetText={editor.budgetText}
          onBudgetChange={editor.setBudgetText}
        />
      )}

      {detail?.kind === "category" && (
        <CategoryDetailPanel
          category={detail.category}
          perspectiveId={perspectiveId}
          from={detail.from}
          to={detail.to}
          onClose={() => setDetail(null)}
          onOpenStakeholder={(stakeholderId) => {
            const node = nodes.find((n) => n.stakeholder?.id === stakeholderId);
            setDetail({
              kind: "stakeholder",
              stakeholder: { id: stakeholderId, name: node?.stakeholder?.name ?? "" },
              month: detail.from.slice(0, 7),
              highlightCategoryId: detail.category.id,
            });
          }}
        />
      )}

      {detail?.kind === "stakeholder" && (
        <StakeholderDetailPanel
          entry={{ stakeholder: detail.stakeholder }}
          perspectiveId={perspectiveId}
          month={detail.month}
          highlightCategoryId={detail.highlightCategoryId}
          onClose={() => setDetail(null)}
        />
      )}

      {detail?.kind === "month" && (
        <MonthDialog month={detail.month} onClose={() => setDetail(null)} />
      )}

      {editingStakeholderId && (
        <SplitEditor
          entry={buildEntry(editingStakeholderId)}
          categories={categories}
          onClose={() => setEditingStakeholderId(null)}
          onSave={(allocations) => {
            applySlices(editingStakeholderId, allocations, "Allocation saved");
            setEditingStakeholderId(null);
          }}
        />
      )}

      {/* In-flight indicator for a drop / split save. The write is async and the
          moved row only appears after refresh() (and may land in a collapsed
          category), so without this a successful drop looks like nothing. */}
      {saving && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-4 z-[100] flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-lg"
        >
          <span
            aria-hidden
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700"
          />
          Saving…
        </div>
      )}
    </div>
  );
}

// The totals-row drill-down: all of one month's transactions, reusing the
// dashboard's month-detail list inside an overlay panel.
function MonthDialog({ month, onClose }: { month: string; onClose: () => void }) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className="fixed right-0 top-0 h-full w-[680px] max-w-[92vw] bg-white shadow-2xl z-50 flex flex-col focus:outline-none p-4 overflow-y-auto">
          <Suspense
            fallback={<div className="p-6 text-sm text-gray-400">Loading…</div>}
          >
            <MonthDetail month={month} onClose={onClose} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function GridSkeleton() {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-3 animate-pulse">
      {Array.from({ length: 8 }).map((_, row) => (
        <div key={row} className="flex items-center gap-3 py-2">
          <div className="h-3 bg-gray-100 rounded w-64" />
          <div className="ml-auto flex gap-3">
            {Array.from({ length: 12 }).map((_, col) => (
              <div key={col} className="h-3 w-10 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyPerspective() {
  return (
    <div className="border border-dashed border-gray-200 rounded-lg bg-white p-10 text-center">
      <p className="text-sm text-gray-500">
        This perspective has no categories yet.
      </p>
      <Link
        to="/categorize"
        className="mt-2 inline-block text-sm text-emerald-600 hover:text-emerald-800"
      >
        Set up cost centers on the categorize page →
      </Link>
    </div>
  );
}
